import React, { useState, useEffect, useRef, useCallback } from 'react';
import { generateChatMessage } from '../services/openRouterService';
import SearchableSelect from './SearchableSelect';
import { getAvailableModels } from '../services/openRouterService';
import './ScriptEditor.css';

// Import pptxgenjs for PowerPoint generation
let PptxGenJS;
try {
  PptxGenJS = require('pptxgenjs');
} catch (e) {
  console.warn('pptxgenjs not available:', e);
}

const AIChatStandalone = () => {
  // All the same state as ScriptEditor's AI Chat
  const [chatHistory, setChatHistory] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [selectedChatModel, setSelectedChatModel] = useState('deepseek/deepseek-chat-v3.1:free');
  const [chatInput, setChatInput] = useState('');
  const [isChatGenerating, setIsChatGenerating] = useState(false);
  const [chatApiKey, setChatApiKey] = useState('');
  const [availableChatModels, setAvailableChatModels] = useState([]);
  const [editingMessageIndex, setEditingMessageIndex] = useState(null);
  const [editingMessageContent, setEditingMessageContent] = useState('');
  const messagesEndRef = useRef(null);
  const chatMessagesRef = useRef(null);
  const chatInputRef = useRef(null);
  const [showHistoryPanel, setShowHistoryPanel] = useState(false);
  const [editingConversationId, setEditingConversationId] = useState(null);
  const [editingConversationTitle, setEditingConversationTitle] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [historySearchQuery, setHistorySearchQuery] = useState('');

  // Generate title from first user message
  const generateConversationTitle = (messages) => {
    const firstUserMessage = messages.find(msg => msg.role === 'user');
    if (firstUserMessage) {
      // Ensure content is a string
      const content = typeof firstUserMessage.content === 'string' 
        ? firstUserMessage.content 
        : String(firstUserMessage.content || '');
      
      // Check if there are attachments that might have useful names
      if (firstUserMessage.attachments && firstUserMessage.attachments.length > 0) {
        const attachmentNames = firstUserMessage.attachments.map(att => att.name).join(', ');
        if (content.trim()) {
          // Combine content and attachment names
          const combined = `${content} [${attachmentNames}]`;
          const text = combined
            .replace(/\*\*(.*?)\*\*/g, '$1')
            .replace(/\*(.*?)\*/g, '$1')
            .replace(/`(.*?)`/g, '$1')
            .replace(/\n/g, ' ')
            .trim();
          return text.length > 50 ? text.substring(0, 50) + '...' : text || 'New Chat';
        } else {
          // Use attachment name as title
          return attachmentNames.length > 50 ? attachmentNames.substring(0, 50) + '...' : attachmentNames || 'New Chat';
        }
      }
      
      const text = content
        .replace(/\*\*(.*?)\*\*/g, '$1')
        .replace(/\*(.*?)\*/g, '$1')
        .replace(/`(.*?)`/g, '$1')
        .replace(/\n/g, ' ')
        .trim();
      
      // Only return 'New Chat' if text is truly empty
      if (!text || text.length === 0) {
        return 'New Chat';
      }
      
      return text.length > 50 ? text.substring(0, 50) + '...' : text;
    }
    return 'New Chat';
  };

  // Load chat history from localStorage on mount
  useEffect(() => {
    const savedHistory = localStorage.getItem('fiddyscript_chat_history');
    if (savedHistory) {
      try {
        const history = JSON.parse(savedHistory);
        const migratedHistory = history.map(conv => {
          const sanitizedMessages = (conv.messages || []).map(msg => {
            // Ensure content is always a string
            const sanitizedMsg = {
              ...msg,
              content: typeof msg.content === 'string' ? msg.content : String(msg.content || ''),
              role: msg.role || 'user'
            };
            if (msg.attachments && msg.attachments.length > 0) {
              sanitizedMsg.attachments = msg.attachments.map(att => ({
                type: att.type,
                name: att.name,
                mimeType: att.mimeType,
                data: undefined
              }));
            }
            return sanitizedMsg;
          });
          // Regenerate title if it's still "New Chat" or missing
          const newTitle = generateConversationTitle(sanitizedMessages);
          return {
            ...conv,
            messages: sanitizedMessages,
            title: (conv.title && conv.title !== 'New Chat') ? conv.title : newTitle
          };
        });
        setChatHistory(migratedHistory);
        if (migratedHistory.length > 0) {
          const mostRecent = migratedHistory.sort((a, b) => 
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
          )[0];
          setCurrentConversationId(mostRecent.id);
          // Sanitize messages to ensure content is always a string
          const sanitizedMessages = (mostRecent.messages || []).map(msg => ({
            ...msg,
            content: typeof msg.content === 'string' ? msg.content : String(msg.content || ''),
            role: msg.role || 'user'
          }));
          setChatMessages(sanitizedMessages);
        }
      } catch (error) {
        console.error('Error loading chat history:', error);
        try {
          localStorage.removeItem('fiddyscript_chat_history');
          setChatHistory([]);
          setChatMessages([]);
          setCurrentConversationId(null);
        } catch (e) {
          console.error('Error clearing corrupted history:', e);
        }
      }
    }
  }, []);

  // Load API key
  useEffect(() => {
    const key = localStorage.getItem('openrouter_api_key');
    if (key) {
      setChatApiKey(key);
    }
  }, []);

  // Load available models and default model from settings
  useEffect(() => {
    const loadModels = async () => {
      try {
        const models = await getAvailableModels();
        setAvailableChatModels(models);
        
        // Always check for default model from settings first
        const savedDefaultModel = localStorage.getItem('default_ai_model');
        
        // Load default model from settings if no conversation is loaded
        // Only use default model for new conversations, not when loading existing ones
        if (!currentConversationId || chatMessages.length === 0) {
          if (savedDefaultModel && models.find(m => m.id === savedDefaultModel && m.isAvailable !== false)) {
            console.log('✅ Using default model from settings:', savedDefaultModel);
            setSelectedChatModel(savedDefaultModel);
            return; // Use default model, don't override
          } else if (savedDefaultModel) {
            console.log('⚠️ Default model not available:', savedDefaultModel);
          }
        }
        
        // If selected model is unavailable or no default set, check current selection
        const selectedModelObj = models.find(m => m.id === selectedChatModel);
        if (!selectedModelObj || selectedModelObj.isAvailable === false) {
          // If current model is unavailable, try to use default first
          if (savedDefaultModel && models.find(m => m.id === savedDefaultModel && m.isAvailable !== false)) {
            console.log('✅ Switching to default model (current model unavailable):', savedDefaultModel);
            setSelectedChatModel(savedDefaultModel);
          } else {
            // Fallback to first available free model
            const firstFreeAvailable = models.find(m => m.isAvailable !== false && m.isFree === true);
            if (firstFreeAvailable) {
              console.log('⚠️ Using fallback free model:', firstFreeAvailable.id);
              setSelectedChatModel(firstFreeAvailable.id);
            } else {
              const firstAvailable = models.find(m => m.isAvailable !== false);
              if (firstAvailable) {
                console.log('⚠️ Using fallback model:', firstAvailable.id);
                setSelectedChatModel(firstAvailable.id);
              }
            }
          }
        } else if (savedDefaultModel && selectedChatModel !== savedDefaultModel && 
                   models.find(m => m.id === savedDefaultModel && m.isAvailable !== false)) {
          // If we have a valid model selected but it's not the default, and default is available,
          // use default for new conversations (but not when loading existing conversations)
          if (!currentConversationId || chatMessages.length === 0) {
            console.log('✅ Switching to default model (new conversation):', savedDefaultModel);
            setSelectedChatModel(savedDefaultModel);
          }
        }
      } catch (error) {
        console.error('Error loading models:', error);
      }
    };
    loadModels();
  }, [currentConversationId, chatMessages.length]);

  // Save chat history to localStorage
  useEffect(() => {
    // Always save chatHistory, even if empty (to clear corrupted data)
    localStorage.setItem('fiddyscript_chat_history', JSON.stringify(chatHistory));
  }, [chatHistory]);

  // Save current conversation to history when messages change
  useEffect(() => {
    // Save conversation even if it's empty (for new conversations)
    if (currentConversationId) {
      setChatHistory(prev => {
        const sanitizedMessages = chatMessages.map(msg => {
          // Ensure content is always a string
          const sanitizedMsg = {
            ...msg,
            content: typeof msg.content === 'string' ? msg.content : String(msg.content || ''),
            role: msg.role || 'user'
          };
          if (msg.attachments && msg.attachments.length > 0) {
            sanitizedMsg.attachments = msg.attachments.map(att => ({
              type: att.type,
              name: att.name,
              mimeType: att.mimeType,
              data: undefined
            }));
          }
          return sanitizedMsg;
        });
        const generatedTitle = generateConversationTitle(chatMessages);
        const updated = prev.map(conv =>
          conv.id === currentConversationId 
            ? { 
                ...conv, 
                messages: sanitizedMessages, 
                model: selectedChatModel,
                // Always regenerate title if it's still "New Chat" or if we have messages now
                title: (conv.title && conv.title !== 'New Chat') ? conv.title : generatedTitle
              }
            : conv
        );
        if (!updated.find(c => c.id === currentConversationId)) {
          updated.push({
            id: currentConversationId,
            title: generatedTitle,
            timestamp: new Date(),
            model: selectedChatModel,
            messages: sanitizedMessages,
          });
        }
        return updated;
      });
    }
  }, [chatMessages, currentConversationId, selectedChatModel]);

  // Save on window unload to ensure persistence
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Force save current conversation before app closes
      if (currentConversationId && chatMessages.length > 0) {
        const sanitizedMessages = chatMessages.map(msg => ({
          ...msg,
          content: typeof msg.content === 'string' ? msg.content : String(msg.content || ''),
          role: msg.role || 'user'
        }));
        const generatedTitle = generateConversationTitle(chatMessages);
        const currentHistory = JSON.parse(localStorage.getItem('fiddyscript_chat_history') || '[]');
        const updated = currentHistory.map(conv =>
          conv.id === currentConversationId 
            ? { 
                ...conv, 
                messages: sanitizedMessages, 
                model: selectedChatModel,
                // Always regenerate title if it's still "New Chat"
                title: (conv.title && conv.title !== 'New Chat') ? conv.title : generatedTitle
              }
            : conv
        );
        if (!updated.find(c => c.id === currentConversationId)) {
          updated.push({
            id: currentConversationId,
            title: generatedTitle,
            timestamp: new Date(),
            model: selectedChatModel,
            messages: sanitizedMessages,
          });
        }
        localStorage.setItem('fiddyscript_chat_history', JSON.stringify(updated));
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [currentConversationId, chatMessages, selectedChatModel]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, isChatGenerating]);

  // Debug: Monitor chatMessages for objects
  useEffect(() => {
    chatMessages.forEach((msg, idx) => {
      if (msg && msg.content !== undefined && typeof msg.content !== 'string') {
        console.error('🚨 FOUND NON-STRING CONTENT IN STATE!', {
          index: idx,
          role: msg.role,
          content: msg.content,
          contentType: typeof msg.content,
          isObject: typeof msg.content === 'object',
          isArray: Array.isArray(msg.content),
          fullMessage: msg
        });
      }
    });
  }, [chatMessages]);

  // Debug: Monitor chatInput for objects
  useEffect(() => {
    if (chatInput !== undefined && chatInput !== null && typeof chatInput !== 'string') {
      console.error('🚨 CRITICAL: chatInput is NOT a string!', {
        chatInput,
        chatInputType: typeof chatInput,
        chatInputConstructor: chatInput?.constructor?.name,
        stringified: String(chatInput),
        refValue: chatInputRef.current?.value,
        refValueType: typeof chatInputRef.current?.value
      });
      // Try to fix it by reading from ref
      if (chatInputRef.current && typeof chatInputRef.current.value === 'string') {
        console.log('🔧 Fixing chatInput by reading from ref:', chatInputRef.current.value);
        setChatInput(chatInputRef.current.value);
      }
    }
  }, [chatInput]);

  // Focus chat input when conversation changes (e.g. New Chat or switching conversation) so cursor is active immediately
  useEffect(() => {
    const id = setTimeout(() => {
      chatInputRef.current?.focus();
    }, 0);
    return () => clearTimeout(id);
  }, [currentConversationId]);

  // Handle new chat
  const handleNewChat = () => {
    const newConvId = 'conv_' + Date.now();
    setCurrentConversationId(newConvId);
    setChatMessages([]);
    setShowHistoryPanel(false);
    
    // Load default model from settings for new conversations
    const savedDefaultModel = localStorage.getItem('default_ai_model');
    if (savedDefaultModel) {
      // Check if the default model is available in the loaded models
      const modelExists = availableChatModels.find(m => m.id === savedDefaultModel && m.isAvailable !== false);
      if (modelExists) {
        console.log('✅ Setting default model for new chat:', savedDefaultModel);
        setSelectedChatModel(savedDefaultModel);
      } else {
        console.log('⚠️ Default model not available, keeping current selection');
      }
    }
  };

  // Handle select conversation
  const handleSelectConversation = (convId) => {
    const conv = chatHistory.find(c => c.id === convId);
    if (conv) {
      setCurrentConversationId(convId);
      // Sanitize messages to ensure content is always a string
      const sanitizedMessages = (conv.messages || []).map(msg => ({
        ...msg,
        content: typeof msg.content === 'string' ? msg.content : String(msg.content || ''),
        role: msg.role || 'user'
      }));
      setChatMessages(sanitizedMessages);
      setSelectedChatModel(conv.model || selectedChatModel);
      setShowHistoryPanel(false);
    }
  };

  // Handle delete conversation
  const handleDeleteConversation = (convId, e) => {
    e.stopPropagation();
    if (confirm('Are you sure you want to delete this conversation?')) {
      setChatHistory(prev => prev.filter(c => c.id !== convId));
      if (currentConversationId === convId) {
        handleNewChat();
      }
    }
  };

  // Handle rename conversation
  const handleRenameConversation = (convId, newTitle) => {
    setChatHistory(prev => prev.map(conv =>
      conv.id === convId ? { ...conv, title: newTitle } : conv
    ));
    setEditingConversationId(null);
    setEditingConversationTitle('');
  };

  // Generate PowerPoint from AI content
  const generatePowerPoint = useCallback(async (content, fileName) => {
    if (!PptxGenJS) {
      throw new Error('pptxgenjs library not available. Please install it: npm install pptxgenjs');
    }

    const pptx = new PptxGenJS.default();
    
    // Set presentation properties
    pptx.author = 'Fiddyscript AI Content Assistant';
    pptx.company = 'Fiddyscript';
    pptx.title = fileName.replace('.pptx', '').replace(/_/g, ' ');
    pptx.subject = 'AI Generated Presentation';
    
    // Parse content to extract slides
    const contentStr = typeof content === 'string' ? content : String(content || '');
    
    // Split content into potential slides
    // Look for patterns like: Slide 1:, # Title, ## Title, or numbered sections
    const slidePatterns = [
      /(?:^|\n)(?:Slide\s+\d+[:\-]|#+\s+[^\n]+|\d+[\.\)]\s+[A-Z][^\n]+)/gi,
      /(?:^|\n)(?:##\s+[^\n]+)/g, // Markdown H2 headings
      /(?:^|\n)(?:###\s+[^\n]+)/g, // Markdown H3 headings
    ];
    
    // Try to find slide breaks
    let slides = [];
    const lines = contentStr.split('\n');
    let currentSlide = { title: '', content: [] };
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Check if this is a slide title/header
      if (line.match(/^(?:Slide\s+\d+|#+\s+|##\s+|###\s+|\d+[\.\)]\s+[A-Z])/i)) {
        // Save previous slide if it has content
        if (currentSlide.title || currentSlide.content.length > 0) {
          slides.push({ ...currentSlide });
        }
        
        // Start new slide
        currentSlide = {
          title: line.replace(/^(?:Slide\s+\d+[:\-]?\s*|#+\s+|\d+[\.\)]\s*)/i, '').trim(),
          content: []
        };
      } else if (line.length > 0) {
        // Add content to current slide
        currentSlide.content.push(line);
      }
    }
    
    // Add the last slide
    if (currentSlide.title || currentSlide.content.length > 0) {
      slides.push(currentSlide);
    }
    
    // If no slides were detected, create slides from paragraphs
    if (slides.length === 0) {
      const paragraphs = contentStr.split(/\n\s*\n/).filter(p => p.trim().length > 0);
      slides = paragraphs.map((para, idx) => ({
        title: `Slide ${idx + 1}`,
        content: para.split('\n').filter(l => l.trim().length > 0)
      }));
    }
    
    // Ensure we have at least one slide
    if (slides.length === 0) {
      slides = [{
        title: pptx.title || 'Presentation',
        content: contentStr.split('\n').filter(l => l.trim().length > 0)
      }];
    }
    
    // Create slides
    slides.forEach((slideData, index) => {
      const slide = pptx.addSlide();
      
      // Add title
      slide.addText(slideData.title, {
        x: 0.5,
        y: 0.5,
        w: 9,
        h: 0.8,
        fontSize: 32,
        bold: true,
        color: '363636',
        align: 'left'
      });
      
      // Process content
      const bulletPoints = [];
      const paragraphs = [];
      
      slideData.content.forEach(line => {
        const trimmed = line.trim();
        // Check if it's a bullet point
        if (trimmed.match(/^[-•*]\s+/) || trimmed.match(/^\d+[\.\)]\s+/)) {
          bulletPoints.push(trimmed.replace(/^[-•*\d+\.\)]\s+/, ''));
        } else if (trimmed.length > 0) {
          paragraphs.push(trimmed);
        }
      });
      
      // Add bullet points if available
      if (bulletPoints.length > 0) {
        slide.addText(bulletPoints, {
          x: 0.5,
          y: 1.5,
          w: 9,
          h: 5,
          fontSize: 18,
          bullet: true,
          color: '363636',
          align: 'left',
          valign: 'top'
        });
      } else if (paragraphs.length > 0) {
        // Add paragraphs as text
        slide.addText(paragraphs.join('\n'), {
          x: 0.5,
          y: 1.5,
          w: 9,
          h: 5,
          fontSize: 18,
          color: '363636',
          align: 'left',
          valign: 'top'
        });
      }
    });
    
    // Generate the PowerPoint file
    return pptx;
  }, []);

  // Download function for file references in chat
  const downloadFile = useCallback(async (fileName, fileType = 'pptx', aiContent = null) => {
    try {
      // Generate filename with timestamp if needed
      const timestamp = Date.now();
      let finalFileName = fileName;
      
      // If it's a sandbox URL, extract the filename
      if (fileName.startsWith('sandbox:/')) {
        finalFileName = fileName.replace('sandbox:/', '').trim();
      }
      
      // Ensure filename has proper extension
      if (!finalFileName.includes('.')) {
        finalFileName = `${finalFileName}.${fileType}`;
      }
      
      // For PowerPoint files, generate actual PPTX
      if (fileType === 'pptx' || finalFileName.toLowerCase().endsWith('.pptx')) {
        try {
          // Try to get AI content from the current conversation
          let contentToUse = aiContent;
          
          // If no content provided, try to get it from the last assistant message
          if (!contentToUse && chatMessages && chatMessages.length > 0) {
            const lastAssistantMsg = [...chatMessages].reverse().find(msg => msg.role === 'assistant');
            if (lastAssistantMsg) {
              contentToUse = lastAssistantMsg.content;
            }
          }
          
          // If still no content, use a default message
          if (!contentToUse) {
            contentToUse = `Slide 1: ${finalFileName.replace('.pptx', '')}\n\nThis presentation was generated by Fiddyscript AI Content Assistant.\n\nPlease provide more detailed content for a better presentation.`;
          }
          
          // Generate PowerPoint
          const pptx = await generatePowerPoint(contentToUse, finalFileName);
          
          // Save the file
          await pptx.writeFile({ fileName: finalFileName });
          
          alert(`✅ PowerPoint file "${finalFileName}" generated successfully!`);
        } catch (pptxError) {
          console.error('Error generating PowerPoint:', pptxError);
          
          // Fallback: Create a placeholder text file
          const content = `Error generating PowerPoint file: ${pptxError.message}\n\n` +
            `File: ${finalFileName}\n` +
            `Original reference: ${fileName}\n\n` +
            `Please ensure pptxgenjs is installed: npm install pptxgenjs`;
          
          const blob = new Blob([content], { type: 'text/plain' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = finalFileName.replace('.pptx', '.txt');
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          
          setTimeout(() => {
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          }, 100);
          
          alert(`⚠️ Could not generate PowerPoint. Error: ${pptxError.message}`);
        }
      } else {
        // For other file types, create a placeholder
        const content = `Placeholder file for: ${finalFileName}\n\nOriginal reference: ${fileName}`;
        const blob = new Blob([content], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = finalFileName;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, 100);
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Error downloading file. Please try again.');
    }
  }, [chatMessages, generatePowerPoint]);

  // Download function for generated images in chat
  const downloadGeneratedImage = useCallback(async (imageUrl, alt = 'Generated Image') => {
    try {
      // Generate filename with timestamp
      const timestamp = Date.now();
      const extension = imageUrl.includes('data:image/') 
        ? imageUrl.match(/data:image\/([^;]+)/)?.[1] || 'png'
        : 'png';
      const fileName = `ai-generated-image-${timestamp}.${extension}`;

      if (imageUrl.startsWith('data:image/')) {
        // Base64 image - convert directly to blob without fetch (to avoid CSP issues)
        // Extract base64 data and mime type
        const matches = imageUrl.match(/^data:image\/([^;]+);base64,(.+)$/);
        if (!matches) {
          throw new Error('Invalid base64 image format');
        }
        
        const mimeType = matches[1];
        const base64Data = matches[2];
        
        // Convert base64 to binary
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: `image/${mimeType}` });
        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        
        // Clean up after a short delay
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, 100);
      } else {
        // External URL - fetch and download
        fetch(imageUrl)
          .then(response => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return response.blob();
          })
          .then(blob => {
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            setTimeout(() => {
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);
            }, 100);
          })
          .catch(error => {
            console.error('Error downloading image:', error);
            // Fallback: open in new tab
            window.open(imageUrl, '_blank');
          });
      }
    } catch (error) {
      console.error('Error in downloadGeneratedImage:', error);
      // Fallback: open in new tab
      window.open(imageUrl, '_blank');
    }
  }, []);

  // Make download function available globally for inline handlers
  useEffect(() => {
    window.downloadGeneratedImage = downloadGeneratedImage;
    return () => {
      delete window.downloadGeneratedImage;
    };
  }, [downloadGeneratedImage]);

  // Set up event delegation for download buttons
  useEffect(() => {
    const handleDownloadClick = (e) => {
      // Check if clicked element is a download button or inside one
      const downloadImageButton = e.target.closest('.chat-download-image-button');
      const downloadFileButton = e.target.closest('.chat-download-file-button');
      
      if (downloadImageButton) {
        // Aggressively prevent all default behaviors and propagation
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        // Cancel the event completely
        if (e.cancelable) {
          e.cancelBubble = true;
        }
        
        // Get image URL from data attribute
        const imageUrl = downloadImageButton.getAttribute('data-image-url');
        const imageAlt = downloadImageButton.getAttribute('data-image-alt') || 'Generated Image';
        
        if (imageUrl) {
          // Decode the URL if it was encoded
          const decodedUrl = decodeURIComponent(imageUrl);
          downloadGeneratedImage(decodedUrl, imageAlt).catch(error => {
            console.error('Error downloading image:', error);
          });
        } else {
          console.error('No image URL found in download button');
        }
      } else if (downloadFileButton) {
        // Handle file download
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        // Cancel the event completely
        if (e.cancelable) {
          e.cancelBubble = true;
        }
        
        const fileRef = downloadFileButton.getAttribute('data-file-ref');
        const fileName = downloadFileButton.getAttribute('data-file-name');
        const fileType = downloadFileButton.getAttribute('data-file-type') || 'pptx';
        
        if (fileRef) {
          const decodedRef = decodeURIComponent(fileRef);
          const decodedName = decodeURIComponent(fileName);
          
          // Try to find the message content that contains this file reference
          let aiContent = null;
          if (chatMessages && chatMessages.length > 0) {
            // Find the assistant message that contains this file reference
            const messageWithFile = [...chatMessages].reverse().find(msg => 
              msg.role === 'assistant' && 
              msg.content && 
              (typeof msg.content === 'string' ? msg.content : String(msg.content || '')).includes(decodedRef)
            );
            if (messageWithFile) {
              aiContent = typeof messageWithFile.content === 'string' 
                ? messageWithFile.content 
                : String(messageWithFile.content || '');
            }
          }
          
          downloadFile(decodedRef, fileType, aiContent).catch(error => {
            console.error('Error downloading file:', error);
          });
        } else {
          console.error('No file reference found in download button');
        }
      }
    };
    
    if (chatMessagesRef.current) {
      chatMessagesRef.current.addEventListener('click', handleDownloadClick, true);
      return () => {
        if (chatMessagesRef.current) {
          chatMessagesRef.current.removeEventListener('click', handleDownloadClick, true);
        }
      };
    }
  }, [downloadGeneratedImage, downloadFile]);

  // Make downloadFile function available globally for inline handlers
  useEffect(() => {
    window.downloadFile = downloadFile;
    return () => {
      delete window.downloadFile;
    };
  }, [downloadFile]);

  // Format message content
  const formatMessageContent = (text) => {
    if (!text) return { __html: '' };
    // Ensure text is a string
    const textStr = typeof text === 'string' ? text : String(text || '');
    if (!textStr) return { __html: '' };
    const images = [];
    const files = []; // Track file references
    const seenImageUrls = new Set(); // Track seen image URLs to prevent duplicates
    const seenFileRefs = new Set(); // Track seen file references to prevent duplicates
    let processedText = textStr;
    
    // Extract markdown images ![alt](url) - deduplicate by URL
    processedText = processedText.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (match, alt, url) => {
      // Normalize URL (remove query params for comparison, but keep original for display)
      const normalizedUrl = url.split('?')[0].split('#')[0];
      if (!seenImageUrls.has(normalizedUrl)) {
        seenImageUrls.add(normalizedUrl);
        images.push({ url, alt: alt || 'Image' });
        return `__IMAGE_PLACEHOLDER_${images.length - 1}__`;
      }
      // Duplicate image - remove it
      return '';
    });
    
    // Extract plain image URLs - deduplicate by URL
    processedText = processedText.replace(/(https?:\/\/[^\s]+\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?[^\s]*)?)/gi, (match, url) => {
      const normalizedUrl = url.split('?')[0].split('#')[0];
      if (!seenImageUrls.has(normalizedUrl)) {
        seenImageUrls.add(normalizedUrl);
        images.push({ url, alt: 'Image' });
        return `__IMAGE_PLACEHOLDER_${images.length - 1}__`;
      }
      // Duplicate image - remove it
      return '';
    });
    
    // Extract base64 image data (data:image/...;base64,...)
    processedText = processedText.replace(/(data:image\/[^;]+;base64,[A-Za-z0-9+/=\s]+)/g, (match, dataUrl) => {
      const cleanUrl = dataUrl.trim();
      const normalizedUrl = cleanUrl.substring(0, 200) + cleanUrl.substring(cleanUrl.length - 50); // Use shorter identifier for comparison
      if (!seenImageUrls.has(normalizedUrl)) {
        seenImageUrls.add(normalizedUrl);
        images.push({ url: cleanUrl, alt: 'Generated Image' });
        return `__IMAGE_PLACEHOLDER_${images.length - 1}__`;
      }
      return match;
    });
    
    // Extract file references - sandbox URLs, markdown file links, etc.
    // Pattern: sandbox:/filename.ext or [filename](sandbox:/filename.ext) or similar patterns
    processedText = processedText.replace(/sandbox:\/([^\s\)\]]+\.(pptx?|docx?|pdf|xlsx?|txt|csv|json|xml|zip|rar|tar|gz))/gi, (match, fileName) => {
      const normalizedRef = fileName.toLowerCase();
      if (!seenFileRefs.has(normalizedRef)) {
        seenFileRefs.add(normalizedRef);
        const fileExt = fileName.split('.').pop().toLowerCase();
        files.push({ 
          ref: match, 
          fileName: fileName,
          fileType: fileExt
        });
        return `__FILE_PLACEHOLDER_${files.length - 1}__`;
      }
      return match;
    });
    
    // Also detect markdown file links: [filename](sandbox:/path) or [filename](path)
    processedText = processedText.replace(/\[([^\]]+)\]\(sandbox:\/([^\)]+)\)/gi, (match, linkText, filePath) => {
      const normalizedRef = filePath.toLowerCase();
      if (!seenFileRefs.has(normalizedRef)) {
        seenFileRefs.add(normalizedRef);
        const fileExt = filePath.split('.').pop().toLowerCase();
        files.push({ 
          ref: `sandbox:/${filePath}`, 
          fileName: linkText || filePath,
          fileType: fileExt
        });
        return `__FILE_PLACEHOLDER_${files.length - 1}__`;
      }
      return match;
    });
    
    // Detect file references in text like "Download the file here: [filename.pptx](sandbox:/filename.pptx)"
    processedText = processedText.replace(/\[([^\]]+\.(pptx?|docx?|pdf|xlsx?|txt|csv|json|xml|zip|rar|tar|gz))\]\(sandbox:\/([^\)]+)\)/gi, (match, linkText, ext, filePath) => {
      const normalizedRef = filePath.toLowerCase();
      if (!seenFileRefs.has(normalizedRef)) {
        seenFileRefs.add(normalizedRef);
        files.push({ 
          ref: `sandbox:/${filePath}`, 
          fileName: linkText || filePath,
          fileType: ext.toLowerCase()
        });
        return `__FILE_PLACEHOLDER_${files.length - 1}__`;
      }
      return match;
    });
    
    let formatted = processedText
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/`(.*?)`/g, '<code>$1</code>')
      .replace(/\n/g, '<br>');
    
    // Replace image placeholders
    images.forEach((img, idx) => {
      const escapedUrl = img.url.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
      const escapedAlt = (img.alt || 'Image').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
      // For data attributes, we need to HTML encode the URL properly
      // Base64 URLs can be very long, so we'll use a data attribute with proper encoding
      const dataUrlEncoded = encodeURIComponent(img.url);
      formatted = formatted.replace(
        `__IMAGE_PLACEHOLDER_${idx}__`,
        `<div class="chat-generated-image">
          <img src="${escapedUrl}" alt="${escapedAlt}" onerror="console.error('Image load error:', this.src); this.style.display='none'; this.nextElementSibling.style.display='block';" />
          <div style="display:none; color: #dc3545; font-size: 12px; padding: 8px;">Failed to load image</div>
          <button class="chat-download-image-button" data-image-url="${dataUrlEncoded}" data-image-alt="${escapedAlt}" type="button" onclick="event.preventDefault(); event.stopPropagation(); event.stopImmediatePropagation(); if(window.downloadGeneratedImage){const url=decodeURIComponent('${dataUrlEncoded}'); window.downloadGeneratedImage(url, '${escapedAlt}');} return false;">⬇️ Download</button>
        </div>`
      );
    });
    
    // Replace file placeholders with download buttons
    files.forEach((file, idx) => {
      const escapedFileName = file.fileName.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
      const escapedRef = file.ref.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
      const fileRefEncoded = encodeURIComponent(file.ref);
      const fileNameEncoded = encodeURIComponent(file.fileName);
      formatted = formatted.replace(
        `__FILE_PLACEHOLDER_${idx}__`,
        `<div class="chat-generated-file" style="margin: 10px 0; padding: 10px; border: 1px solid #ddd; border-radius: 4px; background: #f9f9f9;">
          <div style="display: flex; align-items: center; gap: 10px;">
            <span style="font-size: 24px;">📄</span>
            <div style="flex: 1;">
              <div style="font-weight: bold; color: #333;">${escapedFileName}</div>
              <div style="font-size: 12px; color: #666; margin-top: 2px;">${escapedRef}</div>
            </div>
            <button class="chat-download-file-button" data-file-ref="${fileRefEncoded}" data-file-name="${fileNameEncoded}" data-file-type="${file.fileType}" type="button" onclick="event.preventDefault(); event.stopPropagation(); event.stopImmediatePropagation(); if(window.downloadFile){const ref=decodeURIComponent('${fileRefEncoded}'); const name=decodeURIComponent('${fileNameEncoded}'); const type='${file.fileType}'; window.downloadFile(ref, type);} return false;" style="padding: 6px 12px; background: #007bff; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 14px;">⬇️ Download</button>
          </div>
        </div>`
      );
    });
    
    return { __html: formatted, files };
  };

  // Copy message
  const copyMessage = async (content, button) => {
    try {
      // Ensure content is a string before processing
      const contentStr = typeof content === 'string' ? content : String(content || '');
      const plainText = contentStr.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
      
      let copySuccess = false;
      
      // Try Electron clipboard API first (most reliable in Electron apps)
      if (window.electronAPI && window.electronAPI.copyToClipboard) {
        try {
          window.electronAPI.copyToClipboard(plainText);
          copySuccess = true;
        } catch (electronError) {
          console.warn('Electron clipboard API failed:', electronError);
        }
      }
      
      // Fallback 1: Try modern clipboard API
      if (!copySuccess && navigator.clipboard && navigator.clipboard.writeText) {
        try {
          await navigator.clipboard.writeText(plainText);
          copySuccess = true;
        } catch (clipboardError) {
          console.warn('Modern clipboard API failed:', clipboardError);
        }
      }
      
      // Fallback 2: Use legacy execCommand method (most compatible)
      if (!copySuccess) {
        const textArea = document.createElement('textarea');
        textArea.value = plainText;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        textArea.style.opacity = '0';
        textArea.style.pointerEvents = 'none';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        textArea.setSelectionRange(0, 99999); // For mobile devices
        
        try {
          const successful = document.execCommand('copy');
          if (successful) {
            copySuccess = true;
          } else {
            throw new Error('execCommand copy returned false');
          }
        } catch (execError) {
          console.warn('execCommand copy failed:', execError);
          throw execError;
        } finally {
          document.body.removeChild(textArea);
        }
      }
      
      if (copySuccess) {
        const originalText = button.textContent;
        button.textContent = '✓ Copied';
        setTimeout(() => {
          button.textContent = originalText;
        }, 2000);
      } else {
        throw new Error('All copy methods failed');
      }
    } catch (error) {
      console.error('Error copying:', error);
      // Show user-friendly error message
      const originalText = button.textContent;
      button.textContent = '❌ Failed';
      setTimeout(() => {
        button.textContent = originalText;
      }, 2000);
    }
  };

  // Add to Editor (copy content to clipboard with HTML formatting)
  const addToEditor = async (messageContent) => {
    try {
      // Ensure content is a string
      const contentStr = typeof messageContent === 'string' ? messageContent : String(messageContent || '');
      
      // Convert markdown-like formatting to HTML
      let htmlContent = contentStr
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/`(.*?)`/g, '<code>$1</code>')
        .replace(/\n/g, '<br>');
      
      // Preserve image tags if present
      if (htmlContent.includes('<img')) {
        // Images are already in HTML format, keep them
      } else {
        // Convert markdown images to HTML img tags
        htmlContent = htmlContent.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" />');
      }
      
      const plainText = contentStr.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
      let copySuccess = false;
      
      // Try Electron clipboard API first (most reliable in Electron apps)
      if (window.electronAPI && window.electronAPI.copyToClipboard) {
        try {
          // Electron clipboard API - copy plain text (HTML paste will be handled by editor)
          window.electronAPI.copyToClipboard(plainText);
          copySuccess = true;
        } catch (electronError) {
          console.warn('Electron clipboard API failed:', electronError);
        }
      }
      
      // Fallback 1: Try modern clipboard API with HTML
      if (!copySuccess && navigator.clipboard && navigator.clipboard.write) {
        try {
          const blob = new Blob([htmlContent], { type: 'text/html' });
          const data = new ClipboardItem({ 'text/html': blob });
          await navigator.clipboard.write([data]);
          // Also copy plain text as fallback
          await navigator.clipboard.writeText(plainText);
          copySuccess = true;
        } catch (clipboardError) {
          console.warn('Modern clipboard API failed:', clipboardError);
        }
      }
      
      // Fallback 2: Try plain text only with modern API
      if (!copySuccess && navigator.clipboard && navigator.clipboard.writeText) {
        try {
          await navigator.clipboard.writeText(plainText);
          copySuccess = true;
        } catch (clipboardError) {
          console.warn('Modern clipboard API (text only) failed:', clipboardError);
        }
      }
      
      // Fallback 3: Use legacy execCommand method
      if (!copySuccess) {
        const textArea = document.createElement('textarea');
        textArea.value = plainText;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        textArea.style.opacity = '0';
        textArea.style.pointerEvents = 'none';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        textArea.setSelectionRange(0, 99999);
        
        try {
          const successful = document.execCommand('copy');
          if (successful) {
            copySuccess = true;
          } else {
            throw new Error('execCommand copy returned false');
          }
        } catch (execError) {
          console.warn('execCommand copy failed:', execError);
          throw execError;
        } finally {
          document.body.removeChild(textArea);
        }
      }
      
      if (copySuccess) {
        alert('Content copied to clipboard! You can now paste it into the Content Editor.');
      } else {
        throw new Error('All copy methods failed');
      }
    } catch (error) {
      console.error('Error adding to editor:', error);
      alert('Failed to copy content. Please try selecting and copying manually.');
    }
  };

  // Start editing message
  const startEditingMessage = (index, content) => {
    setEditingMessageIndex(index);
    // CRITICAL: Ensure content is always a string before processing
    let contentStr = '';
    if (content !== undefined && content !== null) {
      if (typeof content === 'string') {
        contentStr = content;
      } else {
        console.error('⚠️ startEditingMessage received non-string content:', {
          index,
          content,
          contentType: typeof content
        });
        try {
          contentStr = typeof content.toString === 'function' 
            ? content.toString() 
            : JSON.stringify(content);
        } catch (e) {
          contentStr = String(content);
        }
      }
    }
    setEditingMessageContent(contentStr.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' '));
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingMessageIndex(null);
    setEditingMessageContent('');
  };

  // Save edited message
  const saveEditedMessage = async () => {
    if (editingMessageIndex === null) return;
    
    // CRITICAL: Ensure editedContent is always a string
    let editedContent = '';
    if (editingMessageContent !== undefined && editingMessageContent !== null) {
      editedContent = typeof editingMessageContent === 'string' 
        ? editingMessageContent 
        : String(editingMessageContent || '');
    }
    editedContent = editedContent.trim();
    
    if (!editedContent) return;
    
    // Sanitize all messages before updating
    const sanitizedMessages = chatMessages.map(msg => ({
      ...msg,
      content: typeof msg.content === 'string' ? msg.content : String(msg.content || '')
    }));
    const updatedMessages = [...sanitizedMessages];
    updatedMessages[editingMessageIndex] = {
      ...updatedMessages[editingMessageIndex],
      content: editedContent // Explicitly ensure it's a string
    };
    setChatMessages(updatedMessages);
    setEditingMessageIndex(null);
    setEditingMessageContent('');
    // Regenerate response
    await sendChatMessage(editedContent);
  };

  // Handle file select
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const newFiles = files.map(file => ({
      type: file.type.startsWith('image/') ? 'image' : 'file',
      name: file.name,
      file: file,
      mimeType: file.type
    }));
    setSelectedFiles(prev => [...prev, ...newFiles]);
    e.target.value = '';
  };

  // Remove file
  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Handle chat key press
  const handleChatKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendChatMessage();
    }
  };

  // Send chat message
  const sendChatMessage = async (editedContent = null) => {
    // DEBUG: Log chatInput state BEFORE processing
    console.log('🔍 DEBUG sendChatMessage - chatInput state:', {
      chatInput,
      chatInputType: typeof chatInput,
      chatInputIsString: typeof chatInput === 'string',
      chatInputValue: String(chatInput),
      editedContent,
      editedContentType: typeof editedContent
    });
    
    // Get message content - exactly like ScriptEditor
    // CRITICAL: Always read from textarea ref first to get the actual current value
    // This ensures we always have the real string value, not a stale state value
    let rawInput = '';
    
    // First, try to get from textarea ref (most reliable)
    if (chatInputRef.current && typeof chatInputRef.current.value === 'string') {
      rawInput = chatInputRef.current.value;
    } 
    // If editedContent is provided and is a string, use it (for editing messages)
    else if (editedContent !== null && editedContent !== undefined && typeof editedContent === 'string') {
      rawInput = editedContent;
    }
    // Fallback to chatInput state if it's a string
    else if (typeof chatInput === 'string') {
      rawInput = chatInput;
    }
    // Last resort: empty string
    else {
      console.warn('⚠️ Could not get valid input, using empty string:', {
        editedContent,
        editedContentType: typeof editedContent,
        chatInput,
        chatInputType: typeof chatInput,
        refValue: chatInputRef.current?.value,
        refValueType: typeof chatInputRef.current?.value
      });
      rawInput = '';
    }
    
    // DEBUG: Log raw input (safe logging without JSON.stringify)
    console.log('🔍 DEBUG sendChatMessage - rawInput:', {
      rawInput,
      rawInputType: typeof rawInput,
      isString: typeof rawInput === 'string',
      stringified: String(rawInput),
      length: rawInput.length
    });
    
    // CRITICAL: Ensure messageContent is ALWAYS a string
    let messageContent = '';
    if (typeof rawInput === 'string') {
      messageContent = rawInput;
    } else {
      // This should never happen now, but just in case
      console.error('🚨 CRITICAL: rawInput is STILL not a string after all checks!', {
        rawInput,
        rawInputType: typeof rawInput,
        rawInputConstructor: rawInput?.constructor?.name,
        stringified: String(rawInput)
      });
      messageContent = String(rawInput || '');
    }
    
    // CRITICAL: Ensure messageContent is ALWAYS a string before any operations
    if (typeof messageContent !== 'string') {
      console.error('🚨 messageContent is STILL not a string after conversion!', {
        type: typeof messageContent,
        value: messageContent,
        editedContent,
        chatInput
      });
      messageContent = String(messageContent || '');
    }
    
    // Trim after ensuring it's a string
    messageContent = messageContent.trim();
    
    // DEBUG: Log final messageContent
    console.log('🔍 DEBUG sendChatMessage - final messageContent:', {
      messageContent,
      messageContentType: typeof messageContent,
      messageContentLength: messageContent.length,
      firstChars: messageContent.substring(0, 50)
    });
    
    if (!messageContent && selectedFiles.length === 0) return;
    if (!chatApiKey) {
      alert('Please configure your OpenRouter API key in Settings.');
      return;
    }

    if (!currentConversationId) {
      handleNewChat();
    }

    // CRITICAL: Double-check content is a string before creating message object
    const finalContent = typeof messageContent === 'string' ? messageContent : String(messageContent || '');
    
    // Process attachments - convert files to base64 data (like ScriptEditor does)
    // This is CRITICAL - without this, images won't be sent to the API
    // Also enforce size limits to prevent token overflow
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB max file size
    const MAX_TEXT_CONTENT_LENGTH = 50000; // ~50k characters max for text files (roughly 12k tokens)
    const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB max for images
    
    const attachments = await Promise.all(selectedFiles.map(async (fileObj) => {
      const fileSize = fileObj.file.size;
      
      if (fileObj.type === 'image') {
        // Check image size
        if (fileSize > MAX_IMAGE_SIZE) {
          alert(`Image "${fileObj.name}" is too large (${(fileSize / 1024 / 1024).toFixed(2)}MB). Maximum size is ${(MAX_IMAGE_SIZE / 1024 / 1024).toFixed(0)}MB.`);
          return null;
        }
        
        // Convert image to base64
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            resolve({
              type: 'image',
              name: fileObj.name,
              data: e.target.result, // This includes the data:image/...;base64, prefix
              mimeType: fileObj.mimeType || fileObj.type || 'image/jpeg',
            });
          };
          reader.onerror = (error) => {
            console.error('Error reading image file:', error);
            resolve(null);
          };
          reader.readAsDataURL(fileObj.file);
        });
      } else {
        // Check file size
        if (fileSize > MAX_FILE_SIZE) {
          alert(`File "${fileObj.name}" is too large (${(fileSize / 1024 / 1024).toFixed(2)}MB). Maximum size is ${(MAX_FILE_SIZE / 1024 / 1024).toFixed(0)}MB. Please use a smaller file or split it into parts.`);
          return null;
        }
        
        // Read text file content with truncation for very long files
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            let fileContent = e.target.result;
            
            // Truncate if content is too long to prevent token overflow
            if (fileContent.length > MAX_TEXT_CONTENT_LENGTH) {
              const truncated = fileContent.substring(0, MAX_TEXT_CONTENT_LENGTH);
              const warning = `\n\n[Note: File content was truncated due to length. Original file was ${fileContent.length} characters. Only the first ${MAX_TEXT_CONTENT_LENGTH} characters are included.]`;
              fileContent = truncated + warning;
              alert(`File "${fileObj.name}" is very large. Only the first ${MAX_TEXT_CONTENT_LENGTH.toLocaleString()} characters will be processed to prevent token limit errors.`);
            }
            
            resolve({
              type: 'file',
              name: fileObj.name,
              data: fileContent,
              mimeType: fileObj.mimeType || 'text/plain',
            });
          };
          reader.onerror = (error) => {
            console.error('Error reading text file:', error);
            resolve(null);
          };
          reader.readAsText(fileObj.file);
        });
      }
    })).then(results => results.filter(r => r !== null)); // Filter out any failed reads
    
    // Debug: Log attachments to verify they have data
    console.log('📎 Processed attachments:', {
      count: attachments.length,
      attachments: attachments.map(att => ({
        type: att.type,
        name: att.name,
        hasData: !!att.data,
        dataLength: att.data ? att.data.length : 0,
        dataPreview: att.data ? att.data.substring(0, 100) : 'no data',
        mimeType: att.mimeType
      }))
    });
    
    // Create userMessage exactly like ScriptEditor - simple and direct
    const userMessage = {
      role: 'user',
      content: finalContent, // Explicitly ensure it's a string
      timestamp: new Date(),
      attachments: attachments.length > 0 ? attachments : undefined,
    };
    
    // Debug: Log the user message being created
    console.log('🔍 Creating user message:', {
      content: finalContent,
      contentType: typeof finalContent,
      contentLength: finalContent.length,
      firstChars: finalContent.substring(0, 50)
    });
    
    // Add user message immediately - same pattern as ScriptEditor
    // CRITICAL: Sanitize all messages to ensure content is always a string
    const sanitizedMessages = chatMessages.map(msg => ({
      ...msg,
      content: typeof msg.content === 'string' ? msg.content : String(msg.content || '')
    }));
    const updatedMessages = [...sanitizedMessages, userMessage];
    
    // Debug: Verify userMessage content before setting state
    console.log('💾 Storing user message in state:', {
      content: userMessage.content,
      contentType: typeof userMessage.content,
      isString: typeof userMessage.content === 'string',
      firstChars: String(userMessage.content).substring(0, 50)
    });
    
    setChatMessages(updatedMessages);
    setChatInput('');
    setSelectedFiles([]);
    setIsChatGenerating(true);

    // Create a placeholder assistant message for streaming - same pattern as ScriptEditor
    const assistantMessage = {
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      model: selectedChatModel
    };
    setChatMessages([...updatedMessages, assistantMessage]);

    try {
      // Limit messages to prevent token overflow - only send last 30 messages
      // This ensures we don't exceed context limits even with long conversations
      // Note: Each message can be large (especially with images), so we use a conservative limit
      const messagesToSend = updatedMessages.slice(-30);
      
      console.log(`📤 Sending ${messagesToSend.length} messages to API (out of ${updatedMessages.length} total in current conversation)`);
      console.log(`📊 Current conversation ID: ${currentConversationId}`);
      if (updatedMessages.length > 30) {
        console.warn(`⚠️ Conversation has ${updatedMessages.length} messages, limiting to last 30 to prevent token overflow`);
      }
      
      // Prepare messages array for API with content truncation
      // IMPORTANT: Remove base64 images and truncate long messages to prevent token overflow
      const currentMessageIndex = messagesToSend.length - 1;
      const MAX_CONTENT_LENGTH = 2000; // Max characters per message to prevent overflow
      
      const apiMessages = messagesToSend.map((msg, index) => {
        const isCurrentMessage = index === currentMessageIndex && msg.role === 'user';
        
        // For assistant messages, truncate content and remove image URLs/base64
        if (msg.role === 'assistant') {
          let content = typeof msg.content === 'string' ? msg.content : String(msg.content || '');
          
          // Remove base64 image data (very long strings)
          content = content.replace(/data:image\/[^;]+;base64,[A-Za-z0-9+/=\s]{100,}/g, '[Generated Image]');
          
          // Remove long image URLs
          content = content.replace(/https?:\/\/[^\s]{200,}/g, '[Image URL]');
          
          // Remove markdown image syntax with long URLs
          content = content.replace(/!\[([^\]]*)\]\([^)]{200,}\)/g, '![$1]([Image URL])');
          
          // Truncate if still too long
          if (content.length > MAX_CONTENT_LENGTH) {
            content = content.substring(0, MAX_CONTENT_LENGTH) + '...[truncated]';
          }
          
          return {
            role: msg.role,
            content: content
          };
        }
        
        // For user messages, only include attachments from the current message being sent
        // For previous messages, strip attachment data to prevent token overflow
        if (msg.attachments && msg.attachments.length > 0 && isCurrentMessage) {
          // Current message with attachments - include them
          console.log('📎 Processing message with attachments:', {
            messageIndex: index,
            isCurrentMessage,
            attachmentsCount: msg.attachments.length,
            attachments: msg.attachments.map(att => ({
              type: att.type,
              name: att.name,
              hasData: !!att.data,
              dataLength: att.data ? att.data.length : 0
            }))
          });
          
          const contentArray = [];
          
          const textContent = typeof msg.content === 'string' ? msg.content : String(msg.content || '');
          
          // Check if this is an image generation model (models that generate images)
          const isImageGenerationModel = selectedChatModel && (
            selectedChatModel.toLowerCase().includes('nanobanana') ||
            selectedChatModel.toLowerCase().includes('gemini') ||
            selectedChatModel.toLowerCase().includes('image') ||
            selectedChatModel.toLowerCase().includes('dall-e') ||
            selectedChatModel.toLowerCase().includes('midjourney') ||
            selectedChatModel.toLowerCase().includes('stable-diffusion') ||
            selectedChatModel.toLowerCase().includes('flux')
          );
          
          // For image generation models, put images FIRST, then text
          // This ensures the reference image is properly recognized
          if (isImageGenerationModel) {
            // Add image attachments FIRST (important for image generation models)
            msg.attachments.forEach(att => {
              if (att.type === 'image' && att.data) {
                // Remove data:image/...;base64, prefix if present
                const base64Data = att.data.includes(',') ? att.data.split(',')[1] : att.data;
                contentArray.push({
                  type: 'image_url',
                  image_url: {
                    url: `data:${att.mimeType || 'image/jpeg'};base64,${base64Data}`
                  }
                });
              }
            });
            
            // Then add text content (with explicit reference to the image if needed)
            if (textContent && textContent.trim()) {
              // Check if text already mentions the image/logo/attached - if not, add a reference
              const lowerText = textContent.toLowerCase();
              const hasImageReference = lowerText.includes('image') || 
                                       lowerText.includes('attached') || 
                                       lowerText.includes('reference') ||
                                       lowerText.includes('logo') ||
                                       lowerText.includes('this image') ||
                                       lowerText.includes('the image') ||
                                       lowerText.includes('the logo') ||
                                       lowerText.includes('this logo');
              
              let finalText = textContent.trim();
              if (!hasImageReference && msg.attachments.some(a => a.type === 'image')) {
                // Add explicit reference to the attached image for image generation models
                // Be more specific about including the logo/image in the output
                finalText = `Use the attached image/logo as a reference and include it in the generated image. ${finalText}`;
              }
              
              contentArray.push({
                type: 'text',
                text: finalText
              });
            }
          } else {
            // For regular chat models, text first, then images (standard format)
            if (textContent && textContent.trim()) {
              contentArray.push({
                type: 'text',
                text: textContent.trim()
              });
            }
            
            // Add image attachments
            msg.attachments.forEach(att => {
              if (att.type === 'image' && att.data) {
                // Remove data:image/...;base64, prefix if present
                const base64Data = att.data.includes(',') ? att.data.split(',')[1] : att.data;
                contentArray.push({
                  type: 'image_url',
                  image_url: {
                    url: `data:${att.mimeType || 'image/jpeg'};base64,${base64Data}`
                  }
                });
              }
            });
          }
          
          // Handle file attachments (always after images/text)
          msg.attachments.forEach(att => {
            if (att.type === 'file' && att.data) {
              // For text files, append to text content
              if (contentArray.length > 0 && contentArray[0].type === 'text') {
                contentArray[0] = {
                  type: 'text',
                  text: `${contentArray[0].text}\n\n[File: ${att.name}]\n${att.data}`
                };
              } else {
                contentArray.push({
                  type: 'text',
                  text: `[File: ${att.name}]\n${att.data}`
                });
              }
            }
          });
          
          const finalContent = contentArray.length > 0 ? contentArray : textContent;
          
          // Debug: Log the final content being sent
          if (isCurrentMessage && contentArray.length > 0) {
            console.log('📤 Final API message content:', {
              isArray: Array.isArray(finalContent),
              itemCount: Array.isArray(finalContent) ? finalContent.length : 0,
              items: Array.isArray(finalContent) ? finalContent.map(item => ({
                type: item.type,
                hasText: !!item.text,
                hasImageUrl: !!item.image_url,
                textPreview: item.text ? item.text.substring(0, 100) : null,
                imageUrlPreview: item.image_url?.url ? item.image_url.url.substring(0, 100) : null
              })) : null
            });
          }
          
          return {
            role: msg.role,
            content: finalContent
          };
        } else {
          // Previous message or message without attachments - just return text content
          let content = typeof msg.content === 'string' ? msg.content : String(msg.content || '');
          
          // Remove base64 image data from previous messages
          content = content.replace(/data:image\/[^;]+;base64,[A-Za-z0-9+/=\s]{100,}/g, '[Generated Image]');
          
          // Truncate if too long
          if (content.length > MAX_CONTENT_LENGTH) {
            content = content.substring(0, MAX_CONTENT_LENGTH) + '...[truncated]';
          }
          
          return {
            role: msg.role,
            content: content
          };
        }
      });
      
      // Log API payload size for debugging
      const totalSize = JSON.stringify(apiMessages).length;
      console.log(`📊 Total API payload size: ${totalSize} characters`);
      apiMessages.forEach((msg, idx) => {
        const msgSize = typeof msg.content === 'string' ? msg.content.length : JSON.stringify(msg.content).length;
        console.log(`  Message ${idx + 1} (${msg.role}): ${msgSize} chars`);
        
        // Debug: Check if message contains images for image generation models
        if (Array.isArray(msg.content)) {
          const hasImages = msg.content.some(item => item.type === 'image_url');
          const hasText = msg.content.some(item => item.type === 'text');
          console.log(`    📋 Content array: ${msg.content.length} items (${hasText ? 'text' : ''} ${hasImages ? 'images' : ''})`);
          if (hasImages) {
            msg.content.forEach((item, itemIdx) => {
              if (item.type === 'image_url') {
                const imgUrl = item.image_url?.url || '';
                console.log(`    🖼️ Image ${itemIdx + 1}: ${imgUrl.substring(0, 100)}... (${imgUrl.length} chars)`);
              } else if (item.type === 'text') {
                console.log(`    📝 Text: ${item.text.substring(0, 200)}...`);
              }
            });
          }
        }
      });

      let accumulatedContent = '';
      const response = await generateChatMessage(
        apiMessages,
        selectedChatModel,
        chatApiKey,
        (chunk) => {
          // CRITICAL: Ensure chunk is always a string
          const chunkStr = typeof chunk === 'string' ? chunk : String(chunk || '');
          accumulatedContent += chunkStr;
          setChatMessages(prev => {
            // Sanitize all messages before updating
            const sanitizedPrev = prev.map(msg => ({
              ...msg,
              content: typeof msg.content === 'string' ? msg.content : String(msg.content || '')
            }));
            const newMessages = [...sanitizedPrev];
            const lastMessage = newMessages[newMessages.length - 1];
            if (lastMessage && lastMessage.role === 'assistant') {
              // Ensure content is always a string
              const contentStr = typeof accumulatedContent === 'string' 
                ? accumulatedContent 
                : String(accumulatedContent || '');
              lastMessage.content = contentStr;
            }
            return newMessages;
          });
        }
      );

      setChatMessages(prev => {
        // Sanitize all messages before updating
        const sanitizedPrev = prev.map(msg => ({
          ...msg,
          content: typeof msg.content === 'string' ? msg.content : String(msg.content || '')
        }));
        const newMessages = [...sanitizedPrev];
        const lastMessage = newMessages[newMessages.length - 1];
        if (lastMessage && lastMessage.role === 'assistant') {
          // Ensure content is always a string
          const finalContent = response || accumulatedContent;
          const contentStr = typeof finalContent === 'string' 
            ? finalContent 
            : String(finalContent || '');
          lastMessage.content = contentStr;
        }
        return newMessages;
      });
    } catch (error) {
      console.error('Chat error:', error);
      setChatMessages(prev => {
        // Ensure error message content is always a string
        let errorContent = typeof error.message === 'string' 
          ? error.message 
          : String(error.message || 'Unknown error');
        
        // Provide helpful error messages for common issues
        if (errorContent.includes('maximum context length') || errorContent.includes('token')) {
          errorContent = `Error: Document is too large for analysis. The file exceeds the token limit (200,000 tokens). Please try:\n\n` +
            `1. Use a smaller file or split the document into smaller parts\n` +
            `2. Extract only the relevant sections you need analyzed\n` +
            `3. Use a file size under 5MB for text files\n\n` +
            `Original error: ${errorContent}`;
        }
        
        const errorMessage = {
          role: 'assistant',
          content: `Error: ${errorContent}`,
          timestamp: new Date(),
          isError: true
        };
        return [...prev.slice(0, -1), errorMessage];
      });
    } finally {
      setIsChatGenerating(false);
    }
  };

  return (
    <div className="ai-chat-standalone">
      <div className="ai-chat-standalone-content">
        {/* Chat History Sidebar */}
        {showHistoryPanel && (
          <div className="ai-chat-history-panel">
            <div className="ai-chat-history-header">
              <h4>Chat History</h4>
              <button 
                className="ai-chat-history-close"
                onClick={() => setShowHistoryPanel(false)}
              >
                ×
              </button>
            </div>
            <input
              type="text"
              className="ai-chat-history-search"
              placeholder="Search conversations..."
              value={historySearchQuery}
              onChange={(e) => setHistorySearchQuery(e.target.value)}
            />
            <button 
              className="ai-chat-history-new"
              onClick={handleNewChat}
            >
              + New Chat
            </button>
            <div className="ai-chat-history-list">
              {chatHistory
                .filter(conv => {
                  if (!historySearchQuery) return true;
                  const searchLower = historySearchQuery.toLowerCase();
                  return (conv.title || '').toLowerCase().includes(searchLower) ||
                         (conv.messages || []).some(msg => msg.content.toLowerCase().includes(searchLower));
                })
                .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                .map(conv => (
                  <div
                    key={conv.id}
                    className={`ai-chat-history-item ${currentConversationId === conv.id ? 'active' : ''}`}
                    onClick={() => handleSelectConversation(conv.id)}
                    onDoubleClick={() => {
                      setEditingConversationId(conv.id);
                      setEditingConversationTitle(conv.title || '');
                    }}
                  >
                    <div className="ai-chat-history-item-content">
                      <div className="ai-chat-history-item-title">
                        {conv.title || 'New Chat'}
                      </div>
                      <div className="ai-chat-history-item-meta">
                        {new Date(conv.timestamp).toLocaleDateString()} • {conv.messages?.length || 0} messages
                      </div>
                    </div>
                    <button
                      className="ai-chat-history-item-delete"
                      onClick={(e) => handleDeleteConversation(conv.id, e)}
                    >
                      🗑️
                    </button>
                  </div>
                ))}
            </div>
          </div>
        )}

        <div className="ai-chat-standalone-main-content">
          <div className="ai-chat-welcome-header">
            <div className="ai-chat-welcome-content">
              {chatHistory.length > 0 && (
                <button 
                  className="ai-chat-history-toggle-left"
                  onClick={() => setShowHistoryPanel(!showHistoryPanel)}
                  title="Toggle History"
                >
                  ☰
                </button>
              )}
              <div className="ai-chat-welcome-text">
                <p className="ai-chat-welcome-greeting">Welcome to Fiddy AI Chat</p>
                <h1 className="ai-chat-welcome-title">Ask me anything—I'm here to help!</h1>
              </div>
              <div className="ai-chat-header-actions">
                <div className="ai-chat-new-chat-wrapper">
                  <button className="ai-chat-new-btn-top" onClick={handleNewChat} title="New Chat">
                    + New Chat
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          <div className="ai-chat-messages" ref={chatMessagesRef}>
            {chatMessages.length === 0 ? null : (
              chatMessages.map((msg, idx) => {
                const isEditing = editingMessageIndex === idx;
                // Ensure message content is always a string - critical safety check
                let safeContent = '';
                if (msg && msg.content !== undefined && msg.content !== null) {
                  if (typeof msg.content === 'string') {
                    safeContent = msg.content;
                  } else {
                    // If it's not a string, convert it
                    console.error('⚠️ Message content is not a string!', {
                      idx,
                      role: msg.role,
                      content: msg.content,
                      contentType: typeof msg.content,
                      msg: msg
                    });
                    try {
                      safeContent = typeof msg.content.toString === 'function' 
                        ? msg.content.toString() 
                        : JSON.stringify(msg.content);
                    } catch (e) {
                      safeContent = String(msg.content);
                    }
                  }
                }
                const safeMsg = { ...msg, content: safeContent };
                
                // Final safety check before rendering
                if (typeof safeMsg.content !== 'string') {
                  console.error('🚨 CRITICAL: safeMsg.content is still not a string after sanitization!', {
                    idx,
                    role: safeMsg.role,
                    content: safeMsg.content,
                    contentType: typeof safeMsg.content,
                    safeContent,
                    safeContentType: typeof safeContent
                  });
                  safeMsg.content = String(safeMsg.content || '');
                }
                
                return (
                  <div key={idx} className={`chat-message ${safeMsg.role} ${safeMsg.isError ? 'error' : ''}`}>
                    <div className="chat-message-avatar">
                      {safeMsg.role === 'user' ? '👤' : '🤖'}
                    </div>
                    <div className="chat-message-content-wrapper">
                      {isEditing && safeMsg.role === 'user' ? (
                        <div className="chat-edit-container">
                          <textarea
                            className="chat-edit-input"
                            value={editingMessageContent}
                            onChange={(e) => setEditingMessageContent(e.target.value)}
                            rows={3}
                            autoFocus
                          />
                          <div className="chat-edit-actions">
                            <button 
                              className="chat-edit-save-btn"
                              onClick={saveEditedMessage}
                            >
                              ✓ Save
                            </button>
                            <button 
                              className="chat-edit-cancel-btn"
                              onClick={cancelEditing}
                            >
                              ✕ Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          {safeMsg.attachments && safeMsg.attachments.length > 0 && (
                            <div className="chat-message-attachments">
                              {safeMsg.attachments.map((att, attIdx) => (
                                <div key={attIdx} className="chat-attachment-preview">
                                  {att.type === 'image' ? (
                                    <div className="chat-attachment-image">🖼️</div>
                                  ) : (
                                    <div className="chat-attachment-file">📄</div>
                                  )}
                                  <span className="chat-attachment-name">{att.name}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          <div 
                            className="chat-message-content"
                            dangerouslySetInnerHTML={formatMessageContent(safeMsg.content)}
                          />
                          <div className="chat-message-actions">
                            {safeMsg.role === 'user' ? (
                              <>
                                <button 
                                  className="chat-copy-button"
                                  onClick={(e) => copyMessage(safeMsg.content, e.target)}
                                  title="Copy message"
                                >
                                  📋 Copy
                                </button>
                                <button 
                                  className="chat-edit-button"
                                  onClick={() => startEditingMessage(idx, safeMsg.content)}
                                  title="Edit message"
                                >
                                  ✏️ Edit
                                </button>
                              </>
                            ) : (
                              <>
                                {!safeMsg.isError && (
                                  <button 
                                    className="chat-copy-button"
                                    onClick={(e) => copyMessage(safeMsg.content, e.target)}
                                    title="Copy message"
                                  >
                                    📋 Copy
                                  </button>
                                )}
                                {!safeMsg.isError && (
                                  <button 
                                    className="chat-add-button"
                                    onClick={() => addToEditor(safeMsg.content)}
                                    title="Add to Content Editor"
                                  >
                                    ➕ Add to Editor
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })
            )}
            {isChatGenerating && (
              <div className="chat-message assistant">
                <div className="chat-message-avatar">🤖</div>
                <div className="chat-message-content-wrapper">
                  <div className="chat-message-loading">
                    <span className="loading-dot"></span>
                    <span className="loading-dot"></span>
                    <span className="loading-dot"></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          
          {selectedFiles.length > 0 && (
            <div className="chat-file-previews">
              {selectedFiles.map((file, index) => (
                <div key={index} className="chat-file-preview">
                  {file.type === 'image' ? (
                    <div className="chat-file-preview-image">🖼️</div>
                  ) : (
                    <div className="chat-file-preview-file">📄</div>
                  )}
                  <span className="chat-file-preview-name">{file.name}</span>
                  <button
                    className="chat-file-preview-remove"
                    onClick={() => removeFile(index)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
          
          <div className="ai-chat-input-section">
            <div className="ai-chat-input-wrapper">
              <input
                type="file"
                id="chat-file-input"
                multiple
                accept="image/*,.pdf,.txt,.doc,.docx"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <input
                type="file"
                id="chat-image-input"
                multiple
                accept="image/*"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <textarea
                ref={chatInputRef}
                className="ai-chat-input-large"
                value={typeof chatInput === 'string' ? chatInput : String(chatInput || '')}
                onChange={(e) => {
                  const value = typeof e.target.value === 'string' ? e.target.value : String(e.target.value || '');
                  setChatInput(value);
                }}
                onKeyDown={handleChatKeyPress}
                placeholder="Whatever you need, just ask Fiddy AI"
                rows={4}
                disabled={isChatGenerating}
              />
              <div className="ai-chat-input-footer">
                <div className="ai-chat-char-counter">
                  {chatInput.length}/10,000
                </div>
                <div className="ai-chat-action-buttons">
                  {availableChatModels && availableChatModels.length > 0 && (
                    <div className="ai-chat-model-selector-action-wrapper">
                      <SearchableSelect
                        id="ai-chat-model-selector-action"
                        value={selectedChatModel}
                        onChange={setSelectedChatModel}
                        options={availableChatModels.map(model => ({
                          id: model.id,
                          value: model.id,
                          label: `${model.name} ${model.isFree ? '(FREE)' : '(PAID)'}`,
                        }))}
                        placeholder="Select model..."
                        className="ai-chat-model-selector-action"
                      />
                    </div>
                  )}
                  <label htmlFor="chat-file-input" className="ai-chat-action-btn" title="Attach File (Multiple files supported)">
                    📎 Attach File
                  </label>
                  <label htmlFor="chat-image-input" className="ai-chat-action-btn" title="Upload Image (Multiple images supported)">
                    🖼️ Upload Image
                  </label>
                </div>
                <button
                  className="ai-chat-send-button-large"
                  onClick={(e) => {
                    e.preventDefault();
                    sendChatMessage();
                  }}
                  disabled={(!chatInput.trim() && selectedFiles.length === 0) || isChatGenerating}
                >
                  {isChatGenerating ? '⏳' : '✈️'}
                </button>
              </div>
            </div>
            
            {chatMessages.length === 0 && (
              <div className="ai-chat-explore-section">
                <h3 className="ai-chat-explore-title">Explore by ready prompt</h3>
                <div className="ai-chat-prompt-cards">
                  <div 
                    className="ai-chat-prompt-card"
                    onClick={() => {
                      setChatInput('Help me write and improve my content. I need assistance with: ');
                      chatInputRef.current?.focus();
                    }}
                  >
                    <div className="ai-chat-prompt-icon">✏️</div>
                    <h4 className="ai-chat-prompt-card-title">Writing</h4>
                    <p className="ai-chat-prompt-card-desc">Elevate your writing with tools designed for seamless creation, and sophisticated style refinement.</p>
                  </div>
                  <div 
                    className="ai-chat-prompt-card"
                    onClick={() => {
                      setChatInput('Help me research and analyze information about: ');
                      chatInputRef.current?.focus();
                    }}
                  >
                    <div className="ai-chat-prompt-icon">💡</div>
                    <h4 className="ai-chat-prompt-card-title">Research & Analysis</h4>
                    <p className="ai-chat-prompt-card-desc">Discover, analyze, interpret, and present information with clarity and impact.</p>
                  </div>
                  <div 
                    className="ai-chat-prompt-card"
                    onClick={() => {
                      setChatInput('Help me with programming. I need assistance with: ');
                      chatInputRef.current?.focus();
                    }}
                  >
                    <div className="ai-chat-prompt-icon">💻</div>
                    <h4 className="ai-chat-prompt-card-title">Programming</h4>
                    <p className="ai-chat-prompt-card-desc">Develop robust code, debug effectively, test thoroughly, and expand your expertise.</p>
                  </div>
                  <div 
                    className="ai-chat-prompt-card"
                    onClick={() => {
                      setChatInput('Help me learn new skills and explore ideas about: ');
                      chatInputRef.current?.focus();
                    }}
                  >
                    <div className="ai-chat-prompt-icon">🎓</div>
                    <h4 className="ai-chat-prompt-card-title">Learning Skills</h4>
                    <p className="ai-chat-prompt-card-desc">Embark on a journey of innovation, exploring new ideas and evolving your existing skills.</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {editingConversationId && (
          <div className="rename-modal-overlay" onClick={() => {
            setEditingConversationId(null);
            setEditingConversationTitle('');
          }}>
            <div className="rename-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Rename Conversation</h3>
              <input
                type="text"
                className="rename-modal-input"
                value={editingConversationTitle}
                onChange={(e) => setEditingConversationTitle(e.target.value)}
                placeholder="Enter conversation title"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && editingConversationTitle.trim()) {
                    handleRenameConversation(editingConversationId, editingConversationTitle);
                  } else if (e.key === 'Escape') {
                    setEditingConversationId(null);
                    setEditingConversationTitle('');
                  }
                }}
              />
              <div className="rename-modal-actions">
                <button
                  className="rename-modal-cancel"
                  onClick={() => {
                    setEditingConversationId(null);
                    setEditingConversationTitle('');
                  }}
                >
                  Cancel
                </button>
                <button
                  className="rename-modal-save"
                  onClick={() => {
                    if (editingConversationTitle.trim()) {
                      handleRenameConversation(editingConversationId, editingConversationTitle);
                    }
                  }}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIChatStandalone;

